// Partner tools, part two (same API as the app's provider area): Calendar, Team, Rota & timesheets, Quick replies.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Clock, MessageSquareText, Trash2, UserPlus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

const cx = (...a) => a.filter(Boolean).join(" ");
const inp = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";
const iso = (d) => d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
const Head = ({ icon: Icon, title, sub }) => (
  <div className="mb-6 flex items-start gap-3">
    <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" aria-hidden="true" /></span>
    <div><h1 className="text-2xl font-bold tracking-tight">{title}</h1>{sub ? <p className="text-sm text-muted-foreground">{sub}</p> : null}</div>
  </div>
);
const STATUS = { confirmed: "bg-emerald-100 text-emerald-800", completed: "bg-slate-200 text-slate-800", pending: "bg-amber-100 text-amber-900", no_show: "bg-rose-100 text-rose-800" };

// ---------------------------------------------------------------- Calendar
export function PartnerCalendar() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [day, setDay] = useState(() => iso(new Date()));
  const load = () => api("/provider/calendar").then(setData).catch(() => setData({ items: [], counts: {} }));
  useEffect(() => { load(); }, []);
  const cells = useMemo(() => {
    const first = new Date(month); const start = (first.getDay() + 6) % 7;
    const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    return [...Array(start).fill(null), ...Array.from({ length: days }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1))];
  }, [month]);
  if (!data) return <Skeleton className="h-96 rounded-2xl" />;
  const dayItems = (data.items || []).filter((b) => b.date === day).sort((a, b) => String(a.time_slot || "").localeCompare(String(b.time_slot || "")));
  const done = async (b) => {
    try { await api("/provider/bookings/" + b.id, { method: "PATCH", body: { status: "completed" } }); toast.success("Marked as completed"); load(); }
    catch (e) { toast.error(e.message || "Couldn't update"); }
  };
  return (
    <div data-testid="partner-calendar">
      <Head icon={CalendarDays} title="Calendar" sub="Every booking across all your listings." />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="rounded-2xl border p-4">
          <div className="mb-3 flex items-center justify-between">
            <Button variant="ghost" size="icon" aria-label="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
            <p className="font-semibold">{month.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</p>
            <Button variant="ghost" size="icon" aria-label="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-muted-foreground">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="py-1">{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={"x" + i} />;
              const k = iso(d); const n = (data.counts[k] || {}).total || 0; const on = k === day; const today = k === iso(new Date());
              return (
                <button key={k} type="button" onClick={() => setDay(k)} aria-pressed={on} aria-label={d.toDateString() + ", " + n + " bookings"}
                  className={cx("flex h-14 flex-col items-center justify-center rounded-xl border text-sm transition", on ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted", today && !on && "border-primary/60")}>
                  <span className="font-semibold">{d.getDate()}</span>
                  {n ? <span className={cx("mt-0.5 rounded-full px-1.5 text-[10px] font-bold", on ? "bg-white/25" : "bg-primary/15 text-primary")}>{n}</span> : null}
                </button>
              );
            })}
          </div>
        </div>
        <div className="rounded-2xl border p-4">
          <p className="font-semibold">{new Date(day + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>
          {dayItems.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No bookings this day.</p> : (
            <ul className="mt-3 divide-y">
              {dayItems.map((b) => (
                <li key={b.id} className="flex items-center gap-3 py-3">
                  {b.listing_image ? <img src={b.listing_image} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <span className="h-10 w-10 rounded-lg bg-muted" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{(b.time_slot ? b.time_slot + " · " : "") + b.customer_name}</p>
                    <p className="truncate text-xs text-muted-foreground">{b.listing_title}</p>
                  </div>
                  <span className={cx("rounded-full px-2 py-0.5 text-[11px] font-semibold", STATUS[b.status] || "bg-muted")}>{String(b.status).replace("_", " ")}</span>
                  {b.status === "confirmed" ? <Button size="sm" variant="outline" onClick={() => done(b)}>Done</Button> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Team
export function PartnerTeam() {
  const [t, setT] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "staff" });
  const load = () => api("/team").then(setT).catch(() => setT({ members: [], seats: {} }));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      if (form.email.trim()) await api("/team/invite", { method: "POST", body: { name: form.name.trim(), email: form.email.trim(), role: form.role } });
      else await api("/team/resource", { method: "POST", body: { name: form.name.trim(), role: form.role } });
      toast.success(form.email ? "Invite sent" : "Added to your team"); setForm({ name: "", email: "", role: "staff" }); load();
    } catch (err) { toast.error(err.message || "Couldn't add"); }
  };
  const remove = async (m) => { if (!window.confirm("Remove " + m.name + "?")) return; try { await api("/team/" + m.id, { method: "DELETE" }); load(); } catch (err) { toast.error(err.message || "Couldn't remove"); } };
  if (!t) return <Skeleton className="h-72 rounded-2xl" />;
  const seats = t.seats || {};
  return (
    <div data-testid="partner-team">
      <Head icon={Users} title="Team" sub={seats.unlimited ? "Unlimited seats on your plan." : (seats.used || 0) + " of " + (seats.capacity || 1) + " seats used."} />
      <ul className="divide-y rounded-2xl border">
        {[t.owner, ...(t.members || [])].filter(Boolean).map((m) => (
          <li key={m.id} className="flex items-center gap-3 p-4">
            {m.avatar ? <img src={m.avatar} alt="" className="h-10 w-10 rounded-full" /> : <span className="grid h-10 w-10 place-items-center rounded-full bg-muted text-sm font-bold">{(m.name || "?").slice(0, 1)}</span>}
            <div className="min-w-0 flex-1"><p className="truncate font-semibold">{m.name}</p><p className="truncate text-xs text-muted-foreground">{[m.role_label, m.email, m.status !== "active" ? m.status : null].filter(Boolean).join(" · ")}</p></div>
            {!m.is_owner ? <Button size="icon" variant="ghost" aria-label={"Remove " + m.name} onClick={() => remove(m)}><Trash2 className="h-4 w-4" /></Button> : null}
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="mt-6 grid gap-3 rounded-2xl border p-4 sm:grid-cols-[1fr_1fr_160px_auto]" aria-label="Add a team member">
        <input className={inp} placeholder="Name" aria-label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input className={inp} placeholder="Email (optional, sends an invite)" aria-label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <select className={inp} aria-label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          <option value="staff">Staff</option><option value="frontdesk">Front desk</option><option value="manager">Manager</option>
        </select>
        <Button type="submit"><UserPlus className="mr-1 h-4 w-4" />Add</Button>
      </form>
      {!t.has_plan && (seats.remaining || 0) <= 0 ? <p className="mt-3 text-sm text-muted-foreground">Need more seats? Upgrade in Plan & billing.</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Rota & timesheets
export function PartnerRota() {
  const [team, setTeam] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [ts, setTs] = useState(null);
  const [f, setF] = useState({ member_id: "", date: iso(new Date()), start: "09:00", end: "17:00" });
  const start = iso(new Date()); const end = iso(new Date(Date.now() + 13 * 864e5));
  const load = () => {
    api("/team").then(setTeam).catch(() => setTeam({ members: [] }));
    api("/provider/shifts?module=services&start=" + start + "&end=" + end).then((r) => setShifts(r.shifts || [])).catch(() => setShifts([]));
    api("/provider/timesheets?start=" + iso(new Date(Date.now() - 6 * 864e5)) + "&end=" + start).then(setTs).catch(() => setTs(null));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const people = team ? [team.owner, ...(team.members || [])].filter(Boolean) : [];
  const add = async (e) => {
    e.preventDefault();
    const m = people.find((p) => p.id === f.member_id);
    if (!m) { toast.error("Pick who's working"); return; }
    try { await api("/provider/shifts", { method: "POST", body: { member_id: m.id, member_name: m.name, module: "services", date: f.date, start: f.start, end: f.end } }); toast.success("Shift added"); load(); }
    catch (err) { toast.error(err.message || "Couldn't add the shift"); }
  };
  const del = async (s) => { try { await api("/provider/shifts/" + s.id, { method: "DELETE" }); load(); } catch (err) { toast.error(err.message || "Couldn't remove"); } };
  const clock = async (m) => { try { const r = await api("/provider/timesheets/clock", { method: "POST", body: { member_id: m.id, member_name: m.name } }); toast.success(m.name + (r.action === "clock_in" ? " clocked in" : " clocked out")); load(); } catch (err) { toast.error(err.message || "Couldn't clock"); } };
  if (!team) return <Skeleton className="h-72 rounded-2xl" />;
  return (
    <div data-testid="partner-rota">
      <Head icon={Clock} title="Rota & timesheets" sub="Plan the next two weeks and clock people in and out." />
      <form onSubmit={add} className="grid gap-3 rounded-2xl border p-4 sm:grid-cols-[1fr_160px_120px_120px_auto]" aria-label="Add a shift">
        <select className={inp} aria-label="Team member" value={f.member_id} onChange={(e) => setF({ ...f, member_id: e.target.value })}>
          <option value="">Who's working?</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input className={inp} type="date" aria-label="Date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <input className={inp} type="time" aria-label="Start" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
        <input className={inp} type="time" aria-label="End" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} />
        <Button type="submit">Add shift</Button>
      </form>
      <h2 className="mt-8 font-bold">Next two weeks</h2>
      {shifts.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No shifts planned yet.</p> : (
        <ul className="mt-2 divide-y rounded-2xl border">
          {shifts.sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start)).map((s) => (
            <li key={s.id} className="flex items-center gap-3 p-3 text-sm">
              <span className="w-32 font-semibold">{new Date(s.date + "T12:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</span>
              <span className="w-28">{s.start}–{s.end}</span><span className="flex-1 truncate">{s.member_name}</span>
              <Button size="icon" variant="ghost" aria-label="Remove shift" onClick={() => del(s)}><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      )}
      <h2 className="mt-8 font-bold">Clock in / out</h2>
      <div className="mt-2 flex flex-wrap gap-2">{people.map((p) => <Button key={p.id} variant="outline" onClick={() => clock(p)}>{p.name}</Button>)}</div>
      {ts && ts.totals ? <p className="mt-4 text-sm text-muted-foreground">Last 7 days: {Number(ts.totals.hours || 0).toFixed(1)} hours across {ts.totals.entries || 0} entries{ts.totals.pay ? " · £" + Number(ts.totals.pay).toFixed(2) + " pay" : ""}.</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Quick replies
export function PartnerQuickReplies() {
  const [list, setList] = useState(null);
  const [draft, setDraft] = useState("");
  useEffect(() => { api("/provider/quick-replies").then((r) => setList(r.replies || [])).catch(() => setList([])); }, []);
  const save = async (next) => { try { const r = await api("/provider/quick-replies", { method: "PUT", body: { replies: next } }); setList(r.replies); toast.success("Saved"); } catch (e) { toast.error(e.message || "Couldn't save"); } };
  const reset = async () => { try { const r = await api("/provider/quick-replies", { method: "DELETE" }); setList(r.replies); toast.success("Back to the defaults"); } catch (e) { toast.error(e.message || "Couldn't reset"); } };
  if (!list) return <Skeleton className="h-60 rounded-2xl" />;
  return (
    <div data-testid="partner-quick-replies">
      <Head icon={MessageSquareText} title="Quick replies" sub="One-tap answers in your inbox, on the app and the web." />
      <ul className="divide-y rounded-2xl border">
        {list.map((r, i) => (
          <li key={i} className="flex items-center gap-3 p-3 text-sm"><span className="flex-1">{r}</span>
            <Button size="icon" variant="ghost" aria-label="Remove reply" onClick={() => save(list.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button></li>
        ))}
      </ul>
      <form className="mt-4 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) { save([...list, draft.trim()]); setDraft(""); } }}>
        <input className={inp} placeholder="Add a reply" aria-label="New quick reply" value={draft} maxLength={200} onChange={(e) => setDraft(e.target.value)} />
        <Button type="submit">Add</Button>
        <Button type="button" variant="ghost" onClick={reset}>Reset</Button>
      </form>
    </div>
  );
}
